import { Box, Button, Card, CircularProgress, Flex, Icon, Text, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { useTrackingContext } from '../../../providers/tracking'
import { selectAnswerDate, selectAnswerGoal } from '../../../store/selectors/client'
import { selectAffordability } from '../../../store/selectors/buyerInfo'
import useViewport from '../../../hooks/useViewport'
import { useEffectOncePerSessionConditionally } from '../../../hooks'

const MSG = defineMessages({
  title: {
    id: 'SearchProfile.title',
    defaultMessage: 'Your search profile is {percent}% complete'
  },
  description: {
    id: 'SearchProfile.description',
    defaultMessage: 'Help personalize your experience with a few details. You can update this any time.'
  },
  completeProfileButton: {
    id: 'SearchProfile.complete_profile_button',
    defaultMessage: 'Complete profile'
  },
  favoriteMarkets: {
    id: 'SearchProfile.favoriteMarkets',
    defaultMessage: 'Favorite markets'
  },
  goal: {
    id: 'SearchProfile.goal',
    defaultMessage: 'Goal'
  },
  moveDate: {
    id: 'SearchProfile.move_date',
    defaultMessage: 'Move date'
  },
  budget: {
    id: 'SearchProfile.budget',
    defaultMessage: 'Budget'
  }
})

type SearchProfileItemProps = {
  isComplete: boolean
  label: string
  children?: ReactNode
}

const SearchProfileItem = ({ isComplete, label }: SearchProfileItemProps) => {
  return (
    <Flex alignItems='center' mb={2}>
      {isComplete ? (
        <Icon name='check-circle-fa' fontSize='xs' color='success.700' mr={2} />
      ) : (
        <Icon name='times-circle' fontSize='xs' mr={2} />
      )}
      <Text color='neutral.900' size='sm' mb={0} mr={6}>
        {label}
      </Text>
    </Flex>
  )
}

interface SearchProfileOnboardingCompletionCardProps {
  buyerHasSetFavoriteMarkets: boolean
  buyerHasSetGoals: boolean
  buyerHasSetCloseDate: boolean
  buyerHasSetPricePoint: boolean
  setIsShowingSearchProfileOnboarding: (isShowing: boolean) => void
  onOpenFavoriteMarketModal?: () => void
}

export const SearchProfileOnboardingCompletionCard = ({
  buyerHasSetFavoriteMarkets,
  buyerHasSetGoals,
  buyerHasSetCloseDate,
  buyerHasSetPricePoint,
  setIsShowingSearchProfileOnboarding,
  onOpenFavoriteMarketModal
}: SearchProfileOnboardingCompletionCardProps) => {
  const intl = useIntl()
  const viewport = useViewport()

  const calculatePercentComplete = () => {
    const totalItems = 4
    const completedItems = [
      buyerHasSetFavoriteMarkets,
      buyerHasSetGoals,
      buyerHasSetCloseDate,
      buyerHasSetPricePoint
    ].filter(Boolean).length
    return Math.round((completedItems / totalItems) * 100)
  }

  return (
    <Card mb={8} px={12} py={8} bg='neutral.200'>
      <Flex
        textAlign={viewport.isSmall ? 'center' : 'left'}
        justifyContent='space-between'
        flexDirection={viewport.isSmall ? 'column' : 'row'}
        alignItems={viewport.isSmall ? 'unset' : 'center'}
      >
        <Flex alignItems='center' gap={4}>
          {!viewport.isSmall && (
            <CircularProgress
              value={calculatePercentComplete()}
              color='success.700'
              aria-label='search profile completion progress chart'
            />
          )}
          <Box>
            <Box>
              <Text m={0} color='success.700' fontWeight='bold' mb={viewport.isSmall ? 4 : 0}>
                {intl.formatMessage(MSG.title, { percent: calculatePercentComplete() })}
              </Text>
              <Text m={0} size='2xs' color='neutral.700' mb={viewport.isSmall ? 4 : 0}>
                {intl.formatMessage(MSG.description)}
              </Text>
            </Box>
            <Flex mb={3} width='100%' flexDirection={viewport.isSmall ? 'column' : 'row'} alignItems='center'>
              <SearchProfileItem
                isComplete={buyerHasSetFavoriteMarkets}
                label={intl.formatMessage(MSG.favoriteMarkets)}
              />
              <SearchProfileItem isComplete={buyerHasSetGoals} label={intl.formatMessage(MSG.goal)} />
              <SearchProfileItem isComplete={buyerHasSetCloseDate} label={intl.formatMessage(MSG.moveDate)} />
              <SearchProfileItem isComplete={buyerHasSetPricePoint} label={intl.formatMessage(MSG.budget)} />
            </Flex>
          </Box>
        </Flex>

        <Button
          color='brand.700'
          onClick={() => {
            if (buyerHasSetCloseDate && buyerHasSetGoals && buyerHasSetPricePoint) {
              onOpenFavoriteMarketModal?.()
            } else {
              setIsShowingSearchProfileOnboarding(true)
            }
          }}
          _hover={{ color: 'neutral.50', backgroundColor: 'brand.700' }}
          tracking={{
            guid: 'completeSearchProfile',
            ui_context: 'For You Content',
            descriptor: 'Complete Profile Button Clicked'
          }}
        >
          {intl.formatMessage(MSG.completeProfileButton)}
        </Button>
      </Flex>
    </Card>
  )
}

export default SearchProfileOnboardingCompletionCard
